// Shared type vocabulary.

export type PieceType = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L';
export const PIECE_TYPES: readonly PieceType[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];

// 0 = spawn, 1 = right (clockwise), 2 = 180, 3 = left (counter-clockwise)
export type Rotation = 0 | 1 | 2 | 3;

export type Cell = PieceType | 'G' | null; // 'G' = garbage

export interface ActivePiece {
  type: PieceType;
  rotation: Rotation;
  x: number; // anchor x (column) — top-left of bounding box
  y: number; // anchor y (row, increases downward)
}

export type LineClearKind =
  | 'single'
  | 'double'
  | 'triple'
  | 'tetris'
  | 'tspin'
  | 'tspin_single'
  | 'tspin_double'
  | 'tspin_triple'
  | 'mini_tspin'
  | 'mini_tspin_single'
  | 'mini_tspin_double';

export interface LockResult {
  cleared: number[]; // row indices that cleared
  kind: LineClearKind | null;
  isPerfectClear: boolean;
  isBackToBack: boolean;
  comboCount: number; // current combo after this lock (0 = no combo)
  tSpinKind: 'none' | 'mini' | 'full';
  scoreDelta: number;
  levelUp: boolean;
  newLevel: number;
}

export interface GameStats {
  score: number;
  level: number;
  lines: number;
  pieces: number;
  startTime: number; // performance.now() at game start
  elapsedMs: number;
  combo: number;
  backToBack: number;
  /** Total t-spins this run (full + mini). */
  tspins: number;
  /** Full t-spins specifically (3-corner with both front filled or TST-kick promotion). */
  tspinsFull: number;
  /** Mini t-spins specifically. */
  tspinsMini: number;
  /** T-spin singles (full, 1-line clear). */
  tspinSingles: number;
  /** T-spin doubles (full, 2-line clear). */
  tspinDoubles: number;
  /** T-spin triples (full, 3-line clear). */
  tspinTriples: number;
  tetrises: number;
  perfectClears: number;
  holds: number;
  /** Hard drops performed this run. */
  hardDrops: number;
  /** Total cells dropped via hard drop. */
  hardDropCells: number;
  finesse: number; // count of suboptimal placements (lower = better)
  /** Max combo length reached this run. */
  maxCombo: number;
  /** Max b2b chain length reached this run. */
  maxB2B: number;
}

export type GameState = 'menu' | 'countdown' | 'playing' | 'paused' | 'gameover' | 'finished';
export type ScreenName = 'menu' | 'modes' | 'settings' | 'stats' | 'controls' | 'about' | 'game';

export type ModeId = 'marathon' | 'sprint40' | 'ultra2min' | 'zen' | 'master' | 'cheese';

export interface ModeContext {
  // Mutates engine behavior or terminates the run.
  initialLevel?: number;
  // When true, the engine never spawns a game over from top-out (used by Zen).
  noTopout?: boolean;
}

export interface ModeResultDetails {
  reasonText: string;
  primaryMetric: { label: string; value: string };
  secondaryMetrics: { label: string; value: string }[];
  newRecord?: boolean;
  /** Pre-run personal best (raw value, e.g. ms or score). Used by the
   *  results screen to render a "previous best" line + delta. */
  prevBestRaw?: number | null;
  /** Run's PB-comparable value, same units as prevBestRaw. */
  currentRaw?: number | null;
  /** True if the mode's PB is "lower is better" (sprint time, cheese time). */
  pbLowerBetter?: boolean;
}
